import { IConfigurationBase, IDisposable, IAgilePointConfiguration } from '@agilepoint/wat';
import i18next, { i18n } from 'i18next';
import HttpApi from 'i18next-http-backend';

function detectBrowserLanguage() {
    return navigator.language || (navigator.languages || [])[0];
}

export interface ICreateLocalizerOptions {
    configuration: IConfigurationBase;
    defaultCulture?: string;
    namespacesToLoad: string[];
    defaultNamespace?: string;
    waitTillInitialized?: boolean;
    debug?: boolean;
}

export type ITranslateFunction = (key: string | string[], options?: { defaultValue?: string }) => any;

export interface ILocalizer {
    currentLanguage: Readonly<string>;
    dir: Readonly<'ltr' | 'rtl'>;
    translate: ITranslateFunction;
    changeLanguage(language: string): Promise<void>;
    /**
     * Gets fired after initialization.
     */
    subscribe(event: 'initialized', callback: (options: any) => void): IDisposable;

    /**
     * Gets fired on loaded resources.
     */
    subscribe(event: 'loaded', callback: (loaded: boolean) => void): IDisposable;

    /**
     * Gets fired if loading resources failed.
     */
    subscribe(event: 'failedLoading', callback: (lng: string, ns: string, msg: string) => void): IDisposable;

    /**
     * Gets fired on accessing a key not existing.
     */
    subscribe(event: 'missingKey', callback: (lngs: string[], namespace: string, key: string, res: string) => void): IDisposable;

    /**
     * Gets fired when resources got added or removed.
     */
    subscribe(event: 'added' | 'removed', callback: (lng: string, ns: string) => void): IDisposable;

    /**
     * Gets fired when changeLanguage got called.
     */
    subscribe(event: 'languageChanged', callback: (lng: string) => void): IDisposable;

    /**
     * Event listener
     */
    subscribe(event: string, listener: (...args: any[]) => void): IDisposable;
}

function changeLanguage(language: string, translator: i18n): Promise<void> {
    return new Promise((resolve, reject) =>
        translator.changeLanguage(language, (err) => {
            if (err) {
                reject(err);
                return;
            }
            resolve();
        }));
}

function createDisposable(translator: i18n, type: string, listener: any): IDisposable {
    return {
        dispose() {
            translator.off(type, listener);
        }
    };
}

function createLocalizerFrom(translator: i18n): ILocalizer {
    return {
        changeLanguage(language: string): Promise<void> {
            return changeLanguage(language, translator);
        },
        get currentLanguage() {
            return translator.language;
        },
        translate(key: string | string[], options?: { defaultValue?: string }) {
            return translator.t(key, options);
        },
        subscribe(type: string, listener: (...args: any[]) => void) {
            translator.on(type, listener);
            return createDisposable(translator, type, listener);
        },
        get dir() {
            return translator.dir(this.currentLanguage);
        }
    };
}

function noop() { /* No Operation Function */ }

async function detectLanguage(apConfig: IAgilePointConfiguration) {
    const { userDetails, culture } = apConfig;
    return culture || userDetails?.supportedLanguage || detectBrowserLanguage();
}

export async function createLocalizer(options: ICreateLocalizerOptions): Promise<ILocalizer> {
    const { configuration: { agilepoint, basePath, revision = '' }, debug = true } = options;
    const localizer = i18next
        .use(HttpApi)
        .use({
            type: 'languageDetector',
            async: true,
            detect: (callback: (lng: string) => void) => detectLanguage(agilepoint).then(callback),
            init: noop,
            cacheUserLanguage: noop
        });

    const translatorInitPromise = localizer.init({
        backend: {
            loadPath: `${basePath}locales/{{lng}}/{{ns}}.json?${revision}`
        },
        fallbackLng: 'en',

        // load all related languages for fallback to region-specific languages
        load: 'all',

        //  have a common namespace used around the full app
        ns: options.namespacesToLoad,
        defaultNS: options.defaultNamespace || options.namespacesToLoad[0],

        debug,

        interpolation: {
            escapeValue: false //  not needed for react!!
        }
    });

    if (options.waitTillInitialized) {
        await translatorInitPromise;
    }

    return createLocalizerFrom(localizer);
}
